import React, { Fragment, useEffect, useRef, useState } from "react";
import { Row, Col, Card, Button } from "react-bootstrap";
import Axios from "../../config/axios";
import { CommonDataTable } from "../../components/CommonDataTable";
import SearchInput from "../../components/Forms/SearchInput";
import { useDispatch } from "react-redux";
import { endBlockingDialog, endLoading, startBlockingDialog, startLoading } from "../../Redux/loadingSlice";
import useDialogs from "../../hooks/useDialogs";
import { Grid } from "@mui/material";
import TableActions from "../../components/Forms/TableActions";
import { currencyFormate } from "../../Utils/utils";
import SearchIcon from "@mui/icons-material/Search";
import AddLoanProfile from "./AddLoanProfile";
import EditLoanProfile from "./EditLoanProfile";
import { toast } from "react-toastify";

const LoanProfileList = () => {
  const dispatch = useDispatch();
  const { openDialog, closeDialog, isDialogOpen } = useDialogs();

  const abortController = useRef(new AbortController());

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loanProfiles, setLoanProfiles] = useState([]);
  const [totalLoanProfiles, setTotalLoanProfiles] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [sortColumn, setSortColumn] = useState("name");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [selectedLoanProfile, setSelectedLoanProfile] = useState(null);

  const columns = [
    {
      name: "No",
      cell: (_, index) => (page - 1) * rowsPerPage + index + 1,
      width: "52px",
      center: true,
    },
    {
      name: "Name",
      selector: "name",
      cell: (row) => <span>{row.name}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Default",
      selector: "default",
      cell: (row) => <span>{row.default ? "Yes" : "No"}</span>,
      sortable: true,
      minWidth: "100px",
    },
    {
      name: "Origination Fee (%)",
      selector: "originationFeePercentage",
      cell: (row) => <span>{row.originationFeePercentage || "N/A"}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Origination Fee ($)",
      selector: "originationFeeDollar",
      cell: (row) => <span>{currencyFormate(row.originationFeeDollar)}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Interest Rate",
      selector: "interestRate",
      cell: (row) => <span>{row.interestRate || "N/A"}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Loan Installment",
      selector: "loanDuration",
      cell: (row) => <span>{row.loanDuration}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Loan Frequency",
      selector: "loanFrequency",
      cell: (row) => <span>{row.loanFrequency}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Maximum Loan Amount",
      selector: "maximumLoanAmount",
      cell: (row) => <span>{currencyFormate(row.maximumLoanAmount)}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <TableActions
          openEditBox={() => {
            openDialog("EditLoanProfile");
            setSelectedLoanProfile(row);
          }}

          onDeleted={() => {
            deleteLoanProfile(row.id);
          }}
          description="Are you sure you want to proceed?"
          tableName="sector"
        />
      ),
      right: true,
    },
  ];

  const fetchLoanProfiles = () => {
    dispatch(startLoading());
    abortController.current.abort();
    abortController.current = new AbortController();

    Axios.get(
      `/loan-profile?pageNumber=${page}&pageSize=${rowsPerPage}&searchText=${searchText}&shortColumn=${sortColumn}&shortType=${sortOrder}`,
      {
        signal: abortController.current.signal,
      }
    )
      .then((res) => {
        if (res.data.success) {
          setLoanProfiles(res.data.data);
          setTotalLoanProfiles(res.data.totalRecord);
        }
      })
      .catch((error) => {
        if (!error.code || error.code !== "ERR_CANCELED") {
          console.error("Error fetching loan profiles", error);
        }
      })
      .finally(() => {
        dispatch(endLoading());
      });
  };

  useEffect(() => {
    fetchLoanProfiles();

    return () => {
      abortController.current.abort();
    };
  }, [page, rowsPerPage, searchText, sortColumn, sortOrder]);

  const deleteLoanProfile = async (id) => {
    try {
      dispatch(startBlockingDialog());

      const response = await Axios.delete(`/loan-profile/admin/${id}`);

      if (response?.data?.success) {
        toast.success(response?.data?.message);
        fetchLoanProfiles();
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(endBlockingDialog());
    }
  };

  return (
    <Fragment>
      <div className="mt-4 px-4 py-3 d-flex align-items-center justify-content-between circle-container">
        <h4>Loan Profiles</h4>
      </div>

      <Row className="row-sm mt-3">
        <Col sm={12} lg={12}>
          <Card className="custom-card overflow-hidden">
            <Card.Body>
              <Grid container justifyContent="space-between" alignItems="center" mb={4}>
                <Grid item lg={4} md={4} sm={6} xs={12} display="flex" alignItems="center">
                  <SearchInput onChange={setSearchText} />
                  <Button
                    variant="primary"
                    onClick={fetchLoanProfiles}
                    className="btn-icon-text"
                  >
                    <SearchIcon sx={{ color: "white" }} />
                  </Button>
                </Grid>
                <Grid item lg={5} md={8} sm={6} xs={12} container justifyContent="end">
                  <Button
                    variant="primary"
                    onClick={() => openDialog("AddLoanProfile")}
                    className="btn-icon-text"
                  >
                    Add Loan Profile
                  </Button>
                </Grid>
              </Grid>

              <CommonDataTable
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                data={loanProfiles}
                totalRows={totalLoanProfiles}
                columns={columns}
                sortServer={true}
                sortByKey={(selector) => {
                  setSortColumn(selector);
                  setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
                }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {isDialogOpen("AddLoanProfile") && (
        <AddLoanProfile
          open={isDialogOpen("AddLoanProfile")}
          onClose={() => closeDialog("AddLoanProfile")}
          getProfileList={fetchLoanProfiles}
        />
      )}

      {isDialogOpen("EditLoanProfile") && (
        <EditLoanProfile
          open={isDialogOpen("EditLoanProfile")}
          onClose={() => closeDialog("EditLoanProfile")}
          editData={selectedLoanProfile}
          getProfileList={fetchLoanProfiles}
        />
      )}
    </Fragment>
  );
};

export default LoanProfileList;
